import { rem } from "polished";
import styled, { css } from "styled-components";

import { Button } from "@/components/button";
import { RenderHTMLContent } from "@/components/editor/render-html-content/render-html-content-component";
import { BlockPaddingWrapper } from "@/components/editor/shared/block-wrapper";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";

export const Wrapper = styled(BlockPaddingWrapper)`
  .text-primitive.headingTwo {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }
`;

export const TimelineWrapper = withViewportStyle("div")`
  display: flex;
  flex-direction: row;
  gap: ${rem(63)};

  ${(props) => props.$viewport.tablet`
    flex-direction: column;
    gap: ${rem(24)};
  `}
`;

export const ScheduleItemWrapper = withViewportStyle("div")`
  --timelineIndicatorTopOffset: ${rem(24)};

  position: relative;
  padding-left: ${rem(104)};
  margin-bottom: var(--timelineIndicatorTopOffset);
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: var(--timelineIndicatorTopOffset);
  }

  &::before {
    left: 0;
    height: ${rem(9)};
    width: ${rem(9)};
    background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  &::after {
    bottom: 0;
    left: ${rem(4)};
    width: ${rem(1)};
    height: calc(var(--timelineIndicatorTopOffset) + 100%);
    background: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  ${(props) => props.$viewport.tablet`
    --timelineIndicatorTopOffset: ${rem(40)};
  `}
`;

export const TimelineContent = withViewportStyle("div")<{
  hideTabList: boolean;
}>`
  width: 100%;
  display: grid;
  gap: ${rem(16)};
  padding: ${({ hideTabList }) => hideTabList && `0 ${rem(256)} 0 ${rem(137)}`};

  ${ScheduleItemWrapper}:last-of-type {
    margin-bottom: 0;
    &::after {
      display: none;
    }
  }

  ${(props) => props.$viewport.tablet`
    padding: ${({ hideTabList }) => hideTabList && "0"};

    ${ScheduleItemWrapper} { 
      &::before, &::after {
        display: none
      }
      padding: 0;
    }
  `}
`;

export const ScheduleItemTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
`;

export const ScheduleItemSegment = styled.div`
  ${({ theme }) => {
    const background = theme.colors[ColorLabels.PRIMARY];
    const foreground = useInvertedColor(background);

    return css`
      padding: ${rem(12)};
      background: ${background};
      color: ${foreground};
    `;
  }}
`;

export const ScheduleItemDate = styled.div`
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
`;

export const AddToCalendarWrapper = withViewportStyle("div")`
  justify-self: flex-end;
`;

export const ScheduleItemTitle = styled.h3`
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  margin: ${rem(16)} 0;
  font-weight: 500;
  line-height: ${rem(32)};
`;

export const ScheduleItemDescription = withViewportStyle(RenderHTMLContent)`
  color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  margin-bottom: ${rem(24)};
  line-height: ${rem(26)};

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(16)};
  `}
`;

export const AddToCalendarButton = styled(Button)`
  --background: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
  --foreground: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  border: none;
  border-radius: 0;
  background-color: var(--background);
  color: var(--foreground);
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};

  &:hover {
    border: none;
    background-color: var(--background);
    color: var(--foreground);
  }
`;

export const AvatarGroupWrapper = withViewportStyle("div")`
  margin: ${rem(24)} 0;
  padding-left: ${rem(6)};

  ${(props) => props.$viewport.tablet`
    margin: ${rem(16)} 0;
  `}
`;

export const MobileAddToCalendarWrapper = styled.div`
  ${AddToCalendarButton} {
    width: 100%;
    justify-content: center;
  }
`;

export const Title = withViewportStyle("div")`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${rem(40)};

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(24)};
  `}
`;

export const FilterWrapper = withViewportStyle("div")`
  .filter-menu {
    position: relative;
    margin-right: ${rem(16)};

    ${(props) => props.$viewport.mobile`
      margin: 0;
    `}
  }
`;

export const SearchWrapper = withViewportStyle("div")<{
  isEmpty: boolean;
}>`
  position: relative;

  .hpn-Search__Label {
    position: absolute;
    top: -${rem(24)};
    left: 0;

    ${(props) => props.$viewport.mobile`
      position: static;
    `}

    label {
      color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
      font-family: ${({ theme }) =>
        theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
    }
  }

  input {
    font-family: ${({ theme }) =>
      theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
    width: 100%;
    padding-left: ${rem(16)};
    background-color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    caret-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    min-width: ${rem(240)};
    border-radius: 0;
    border: ${rem(2)} solid ${({ theme }) =>
  theme.colors[ColorLabels.SECONDARY]};
  }

  button.hpn-Search__Clear {
    cursor: ${({ isEmpty }) => (isEmpty ? "not-allowed" : "pointer")};

    svg {
      opacity: ${({ isEmpty }) => (isEmpty ? 0.5 : 1)};
    }
  }
  
  .hpn-Search__Submit,
  input:focus + .hpn-Search__Submit {
    background-color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY]};
  }

  input:focus {
    border: ${rem(2)} solid ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  }

  svg {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  }
`;

export const StyledMenuContent = withViewportStyle("div")<{
  $editing: boolean;
}>`
  position: absolute;
  top: 100%;
  right: 0;
  min-width: ${rem(225)};
  background-color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  box-shadow: 0 ${rem(5)} ${rem(20)} rgba(0, 0, 0, 0.33);
  padding: ${rem(24)};
  margin-top: ${rem(8)};
  -webkit-tap-highlight-color: transparent;
  z-index: var(--z-index-highest);

  ${(props) => props.$viewport.mobile`
    height: 100vh;
    width: 100%;
    position: fixed;
    top: 0;
    right: 0;
    z-index: var(--z-index-highest);
    margin-top: unset;

    ${
      props.$editing &&
      `
      max-width: ${rem(364)};
      left: ${rem(48)};
      `
    }
  `}
`;
